import type { LucideIcon } from 'lucide-react';
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

// ------------------------------------------------------------------ Card

export function Card({ className, children, ...rest }: HTMLAttributes<HTMLElement> & { as?: 'section' | 'div' }) {
  return (
    <section className={cn('min-w-0 rounded-lg border border-line bg-surface', className)} {...rest}>
      {children}
    </section>
  );
}

export function CardHeader({ title, subtitle, actions, className }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <header className={cn('flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-line-soft px-5 py-3.5', className)}>
      <div className="flex min-w-0 flex-col">
        <h2 className="text-md font-semibold">{title}</h2>
        {subtitle ? <p className="text-sm text-subtle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export function CardBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('p-5', className)}>{children}</div>;
}

// ------------------------------------------------------------------ Table

export function Table({ children, className, minWidth = 640, label }: { children: ReactNode; className?: string; minWidth?: number; label?: string }) {
  return (
    // The wrapper scrolls on narrow screens; the page itself never scrolls sideways.
    <div className={cn('relative overflow-x-auto', className)} role="region" aria-label={label} tabIndex={label ? 0 : undefined}>
      <table className="w-full border-collapse text-base" style={{ minWidth }}>
        {children}
      </table>
    </div>
  );
}

export function TH({ className, children, ...rest }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th scope="col" className={cn('border-b border-line-soft px-4 py-3 text-start text-sm font-normal whitespace-nowrap text-subtle', className)} {...rest}>
      {children}
    </th>
  );
}

export function TD({ className, children, ...rest }: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={cn('border-b border-line-faint px-4 py-3 align-middle', className)} {...rest}>
      {children}
    </td>
  );
}

export function TR({ className, children, interactive, ...rest }: HTMLAttributes<HTMLTableRowElement> & { interactive?: boolean }) {
  return (
    <tr className={cn(interactive && 'cursor-pointer transition-colors hover:bg-hover', className)} {...rest}>
      {children}
    </tr>
  );
}

// ------------------------------------------------------------------ Badge

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'dark';

const BADGE_TONES: Record<Tone, string> = {
  neutral: 'bg-neutral-tint text-muted',
  success: 'bg-success-tint text-success',
  warning: 'bg-warning-tint text-warning-text',
  danger: 'bg-danger-tint text-danger-text',
  info: 'bg-info-tint text-info-text',
  accent: 'bg-accent text-white',
  dark: 'bg-ink text-white',
};

export function Badge({ tone = 'neutral', icon: Icon, children, className }: { tone?: Tone; icon?: LucideIcon; children: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex w-fit items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-medium whitespace-nowrap', BADGE_TONES[tone], className)}>
      {Icon ? <Icon aria-hidden className="size-3.5" /> : null}
      {children}
    </span>
  );
}

// ------------------------------------------------------------------ Notice

const NOTICE: Record<'info' | 'warning' | 'success' | 'danger', { cls: string; icon: LucideIcon }> = {
  info: { cls: 'bg-info-tint text-info-text', icon: Info },
  warning: { cls: 'bg-warning-tint text-warning-text', icon: TriangleAlert },
  success: { cls: 'bg-success-tint text-success', icon: CircleCheck },
  danger: { cls: 'bg-danger-tint text-danger-text', icon: CircleAlert },
};

export function Notice({
  tone = 'info',
  title,
  children,
  action,
  className,
  role,
}: {
  tone?: keyof typeof NOTICE;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  role?: 'alert' | 'status';
}) {
  const { cls, icon: Icon } = NOTICE[tone];
  return (
    <div role={role} className={cn('flex flex-wrap items-start gap-x-3 gap-y-2 rounded-md px-4 py-3 text-base', cls, className)}>
      <Icon aria-hidden className="mt-0.5 size-[18px] shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className="leading-relaxed">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

// ------------------------------------------------------------------ Stat / KPI

export function Stat({
  label,
  value,
  sub,
  subTone,
  icon: Icon,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  subTone?: 'muted' | 'warning' | 'success' | 'danger';
  icon?: LucideIcon;
  className?: string;
}) {
  return (
    <Card className={cn('flex flex-col gap-1.5 px-5 py-4', className)}>
      <div className="flex items-center justify-between gap-2 text-sm text-subtle">
        <span>{label}</span>
        {Icon ? <Icon aria-hidden className="size-[18px] text-subtle" /> : null}
      </div>
      <div className="text-2xl font-semibold">{value}</div>
      {sub ? (
        <div
          className={cn(
            'text-sm',
            subTone === 'warning' ? 'text-warning-text' : subTone === 'success' ? 'text-success' : subTone === 'danger' ? 'text-danger-text' : 'text-subtle',
          )}
        >
          {sub}
        </div>
      ) : null}
    </Card>
  );
}

// ------------------------------------------------------------------ Description list

export function DL({ items, className, columns = 2 }: { items: { label: ReactNode; value: ReactNode; wide?: boolean }[]; className?: string; columns?: 2 | 3 | 4 }) {
  return (
    <dl className={cn('grid grid-cols-1 gap-x-6 gap-y-4', columns === 2 ? 'sm:grid-cols-2' : columns === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-4', className)}>
      {items.map((item, i) => (
        <div key={i} className={cn('flex min-w-0 flex-col gap-0.5', item.wide && 'sm:col-span-full')}>
          <dt className="text-sm text-subtle">{item.label}</dt>
          <dd className="text-base font-medium break-words">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

// ------------------------------------------------------------------ Progress

export interface Segment {
  /** 0–100 */
  value: number;
  className: string;
}

export function ProgressBar({ segments, label, className, height = 'h-2.5' }: { segments: Segment[]; label: string; className?: string; height?: string }) {
  return (
    <div role="img" aria-label={label} className={cn('flex overflow-hidden rounded-full bg-track', height, className)}>
      {segments.map((s, i) => (
        <div key={i} className={cn('h-full transition-[width]', s.className)} style={{ width: `${Math.max(0, Math.min(100, s.value))}%` }} />
      ))}
    </div>
  );
}

export function Legend({ items }: { items: { label: ReactNode; className: string }[] }) {
  return (
    <ul className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted">
      {items.map((item, i) => (
        <li key={i} className="flex items-center gap-1.5">
          <span aria-hidden className={cn('size-2.5 rounded-xs', item.className)} />
          {item.label}
        </li>
      ))}
    </ul>
  );
}
